import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CourseCard from "@/components/CourseCard";
import { Button } from "@/components/ui/button";
import { ArrowRight, Cpu, Building2, Code2, BrainCircuit, ShieldCheck, Award, Zap, CreditCard, Smartphone, Calculator, BriefcaseBusiness } from "lucide-react";

const CATS = [
  { name: "Inteligencia Artificial", icon: BrainCircuit, color: "#00E5FF", desc: "Machine Learning, LLMs y IA Generativa" },
  { name: "Informática", icon: Cpu, color: "#7000FF", desc: "Ciberseguridad, Data y ofimática avanzada" },
  { name: "Ingeniería de Sistemas", icon: Code2, color: "#00E6BA", desc: "Full Stack, Cloud y DevOps" },
  { name: "Ingeniería Civil", icon: Building2, color: "#FFB020", desc: "BIM, AutoCAD y diseño estructural" },
  { name: "Contabilidad", icon: Calculator, color: "#A3E635", desc: "Costos, tributación y finanzas" },
  { name: "Administración", icon: BriefcaseBusiness, color: "#FB7185", desc: "Gestión, proyectos y liderazgo" },
];

const MARQUEE = ["PYTHON", "REACT", "AUTOCAD", "S10", "NIIF", "PMBOK", "AWS", "REVIT", "ETABS", "POWER BI"];

export default function Landing() {
  const [courses, setCourses] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/courses").then(({ data }) => setCourses(Array.isArray(data) ? data.slice(0, 6) : [])).catch(() => {});
  }, []);

  return (
    <div className="App">
      <Navbar />

      {/* HERO */}
      <section className="relative pt-32 pb-24 overflow-hidden grid-bg">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#00E5FF]/10 rounded-full blur-[140px] -z-0" />
        <div className="max-w-7xl mx-auto px-6 relative grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.span initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-[#00E5FF] border border-[#00E5FF]/30 rounded-full px-4 py-1.5 mb-6">
              <Zap className="w-3.5 h-3.5" /> Los cursos más demandados del 2026
            </motion.span>
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="font-heading font-extrabold text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tighter mb-6">
              Impulsa tu <span className="text-[#00E5FF] text-glow">carrera</span> con capacitación de élite
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
              className="font-body text-lg text-[#8A8D98] max-w-xl mb-8 leading-relaxed">
              Domina la Inteligencia Artificial, Ingeniería Civil, Sistemas e Informática con cursos prácticos, instructores expertos y certificación profesional.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="flex flex-wrap gap-4">
              <Button data-testid="hero-explore-btn" onClick={() => navigate("/cursos")} className="bg-[#00E5FF] text-black font-semibold hover:bg-white rounded-md h-12 px-7 text-base">
                Explorar cursos <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button data-testid="hero-register-btn" onClick={() => navigate("/register")} variant="outline" className="border-white/20 bg-transparent text-white hover:bg-white/5 rounded-md h-12 px-7 text-base">
                Crear cuenta gratis
              </Button>
            </motion.div>
            <div className="flex items-center gap-8 mt-10 font-mono text-xs text-[#525560]">
              <div><span className="text-white text-2xl font-bold block">18k+</span> Alumnos</div>
              <div><span className="text-white text-2xl font-bold block">4.9★</span> Valoración</div>
              <div><span className="text-white text-2xl font-bold block">17+</span> Cursos premium</div>
            </div>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2 }} className="lg:col-span-5 relative">
            <div className="relative rounded-xl overflow-hidden border border-white/10">
              <img src="https://images.unsplash.com/photo-1550751827-4bd374c3f58b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Tecnología" className="w-full h-[420px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
            </div>
          </motion.div>
        </div>

        {/* Marquee */}
        <div className="mt-20 overflow-hidden">
          <div className="flex whitespace-nowrap animate-marquee">
            {[...MARQUEE, ...MARQUEE].map((t, i) => (
              <span key={i} className="font-heading font-extrabold text-6xl text-transparent mx-8" style={{ WebkitTextStroke: "1px rgba(255,255,255,0.06)" }}>{t}</span>
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section id="categorias" className="py-24 max-w-7xl mx-auto px-6">
        <div className="mb-14">
          <span className="font-mono text-xs uppercase tracking-widest text-[#00E5FF]">// Áreas de especialización</span>
          <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight mt-3">Elige tu camino</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {CATS.map((c, i) => (
            <motion.div key={c.name} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              onClick={() => navigate(`/cursos?cat=${encodeURIComponent(c.name)}`)}
              data-testid={`category-${i}`}
              className="cursor-pointer bg-[#0C0D12] border border-white/5 rounded-lg p-8 hover:border-[#00E5FF]/40 hover:-translate-y-1 transition-all duration-300">
              <div className="w-12 h-12 rounded-md flex items-center justify-center mb-6" style={{ backgroundColor: `${c.color}1a` }}>
                <c.icon className="w-6 h-6" style={{ color: c.color }} />
              </div>
              <h3 className="font-heading font-semibold text-xl mb-2">{c.name}</h3>
              <p className="font-body text-sm text-[#8A8D98] leading-relaxed">{c.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FEATURED COURSES */}
      <section className="py-24 max-w-7xl mx-auto px-6">
        <div className="flex items-end justify-between mb-14">
          <div>
            <span className="font-mono text-xs uppercase tracking-widest text-[#00E5FF]">// Más populares</span>
            <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight mt-3">Cursos destacados</h2>
          </div>
          <Link to="/cursos" data-testid="view-all-courses" className="hidden md:flex items-center gap-2 font-mono text-sm text-[#8A8D98] hover:text-[#00E5FF] transition-colors">
            Ver todos <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {courses.map((c) => <CourseCard key={c.id} course={c} />)}
        </div>
      </section>

      {/* WHY US */}
      <section id="por-que" className="py-24 max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-3 gap-8">
          {[
            { icon: Award, title: "Certificación profesional", desc: "Obtén un certificado verificable al completar cada curso." },
            { icon: ShieldCheck, title: "Acceso de por vida", desc: "Compra una vez y accede al contenido para siempre." },
            { icon: CreditCard, title: "Pagos flexibles", desc: "Paga con tarjeta de débito, crédito o Yape de forma segura." },
          ].map((f) => (
            <div key={f.title} className="bg-[#0C0D12] border border-white/5 rounded-lg p-8">
              <f.icon className="w-8 h-8 text-[#00E5FF] mb-5" />
              <h3 className="font-heading font-semibold text-xl mb-2">{f.title}</h3>
              <p className="font-body text-sm text-[#8A8D98] leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 max-w-7xl mx-auto px-6">
        <div className="relative bg-[#0C0D12] border border-[#00E5FF]/20 rounded-2xl p-12 md:p-20 text-center overflow-hidden">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[300px] bg-[#00E5FF]/10 blur-[120px]" />
          <div className="relative">
            <Smartphone className="w-10 h-10 text-[#00E5FF] mx-auto mb-6" />
            <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tight mb-4 max-w-2xl mx-auto">Empieza a aprender hoy mismo</h2>
            <p className="font-body text-[#8A8D98] max-w-xl mx-auto mb-8">Únete a miles de profesionales que ya están transformando su carrera con CapacitaPro.</p>
            <Button data-testid="cta-register-btn" onClick={() => navigate("/register")} className="bg-[#00E5FF] text-black font-semibold hover:bg-white rounded-md h-12 px-8 text-base">
              Crear mi cuenta gratis <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
